import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Layout from "../layout";
import NotFound from "../componants/NotFound";
import PageSkeleton from "../componants/PageSkeleton";
import PrivateRoute from "./PrivateRoute";

// ── Onboarding ───────────────────────────────────────────────────────────────
const Login = lazy(() => import("../onboarding/Login"));
const ForgotPassword = lazy(() => import("../onboarding/ForgotPassword"));
const OTP = lazy(() => import("../onboarding/OTP"));
const NewPassword = lazy(() => import("../onboarding/NewPassword"));
const SignUp = lazy(() => import("../onboarding/SignUp"));

// ── Venue Vendor ───────────────────────────────────────────────────────────

const VendorNotification = lazy(() => import("../header/VendorNotification"));

const VenueDashboard = lazy(
  () => import("../vendors/venueVendor/venueDashboard/VenueDashboard"),
);
const MyListings = lazy(
  () => import("../vendors/venueVendor/myListings/MyListings"),
);
const MyListingsDetail = lazy(
  () => import("../vendors/venueVendor/myListings/MyListingDetail"),
);
const VenueBookings = lazy(
  () => import("../vendors/venueVendor/venueBookings/VenueBookings"),
);
const BookingsOfParticularVenue = lazy(
  () =>
    import("../vendors/venueVendor/venueBookings/BookingsOfParticularVenue"),
);
const VenueBookingDetail = lazy(
  () => import("../vendors/venueVendor/venueBookings/VenueBookingDetail"),
);
const Proposal = lazy(() => import("../vendors/venueVendor/proposal/Proposal"));
const ProposalDetail = lazy(
  () => import("../vendors/venueVendor/proposal/ProposalDetail"),
);
const Negotiation = lazy(
  () => import("../vendors/venueVendor/negotiation/Negotiation"),
);
const NegotiationDetail = lazy(
  () => import("../vendors/venueVendor/negotiation/NegotiationDetail"),
);
const EarningsPayouts = lazy(
  () => import("../vendors/venueVendor/earningPayout/EarningPayout"),
);
const VenueProfile = lazy(
  () => import("../vendors/venueVendor/venueProfile/VenueProfile"),
);
const VenueProfileEdit = lazy(
  () => import("../vendors/venueVendor/venueProfile/VenueProfileEdit"),
);
const VenueBankEdit = lazy(
  () => import("../vendors/venueVendor/venueBank/VenueBankEdit"),
);
const VenueBankDetail = lazy(
  () => import("../vendors/venueVendor/venueBank/VenueBankDetail"),
);

const HotelCreateLayout = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/HotelCreateLayout"),
);

const HotelBasicInfo = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/HotelBasicInfo"),
);
const PropertyAddress = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/PropertyAddress"),
);
const LocationMap = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/LocationMap"),
);
const HotelGallery = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/HotelGallery"),
);
const HotelAmenities = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/Amenities"),
);
const PropertyRules = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/PropertyRules"),
);
const HotelRoomManagement = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/RoomManagement"),
);
const Pricing = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/Pricing"),
);
const Cancellation = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/Cancellation"),
);
const Taxes = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/Taxes"),
);
const NearbyAttractions = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/NearbyAttractions"),
);
const ReviewPublish = lazy(
  () => import("../vendors/hotelVendor/hotel/createHotel/ReviewPublish"),
);

const CreateLayout = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/CreateLayout"),
);
const BasicInfo = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/BasicInfo"),
);
const Location = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Location"),
);
const Capacity = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Capacity"),
);
const Amenities = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Amenities"),
);
const BookingDetail = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/BookingDetail"),
);
const Rules = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Rules"),
);
const Highlights = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Highlights"),
);
const Events = lazy(
  () => import("../vendors/venueVendor/myListings/createListing/Events"),
);
const VenueSetting = lazy(
  () => import("../vendors/venueVendor/venueSetting/VenueSetting"),
);
const Notification = lazy(
  () => import("../vendors/venueVendor/venueSetting/Notification"),
);
const Account = lazy(
  () => import("../vendors/venueVendor/venueSetting/Account"),
);
const Security = lazy(
  () => import("../vendors/venueVendor/venueSetting/Security"),
);
const Preferences = lazy(
  () => import("../vendors/venueVendor/venueSetting/Preferences"),
);

// ── Hotel Vendor ───────────────────────────────────────────────────────────
const HotelDashboard = lazy(
  () => import("../vendors/hotelVendor/hoteldashboard/hotelDashboard"),
);
const RoomManagement = lazy(
  () => import("../vendors/hotelVendor/roomManagement/RoomManagement"),
);
const HotelSettlement = lazy(
  () => import("../vendors/hotelVendor/hotelSettlement/HotelSettlement"),
);
const HotelBookings = lazy(
  () => import("../vendors/hotelVendor/hotelBookings/HotelBookings"),
);
const BookingsOfParticularHotel = lazy(
  () =>
    import("../vendors/hotelVendor/hotelBookings/BookingsOfParticularHotel"),
);

const HotelCustomers = lazy(
  () => import("../vendors/hotelVendor/hotelCustomers/HotelCustomers"),
);
const HotelNotifications = lazy(
  () => import("../vendors/hotelVendor/hotelNotifications/HotelNotifications"),
);
const HotelProfile = lazy(
  () => import("../vendors/hotelVendor/hotelProfile/HotelProfile"),
);
const HotelProfileEdit = lazy(
  () => import("../vendors/hotelVendor/hotelProfile/HotelProfileEdit"),
);
const HotelReviews = lazy(
  () => import("../vendors/hotelVendor/hotelReviews/HotelReviews"),
);
const Hotel = lazy(() => import("../vendors/hotelVendor/hotel/Hotel"));
const HotelDetails = lazy(
  () => import("../vendors/hotelVendor/hotel/HotelDeails"),
);
const HotelSettings = lazy(
  () => import("../vendors/hotelVendor/hotelSettings/HotelSettings"),
);
const DetailsHotelBooking = lazy(
  () => import("../vendors/hotelVendor/hotelBookings/detailsHotalBooking"),
);
const HotelReport = lazy(
  () => import("../vendors/hotelVendor/hotelReport/HotelReport"),
);

// ── Event Vendor ───────────────────────────────────────────────────────────
const EventDashboard = lazy(
  () => import("../vendors/eventVendor/eventDashboard/EventDashboard"),
);
const EventManagement = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/eventManagementTab/EventsTab"),
);
const EventDetail = lazy(
  () => import("../vendors/eventVendor/eventManagement/EventDetail"),
);
const BookingsOfParticularEvent = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/eventManagementTab/BookingsOfParticularEvent"),
);
const BookingTab = lazy(
  () =>
    import("../vendors/eventVendor/eventBooking/eventBookingTab/BookingTab"),
);
const EventBookingDetail = lazy(
  () => import("../vendors/eventVendor/eventBooking/EventBookingDetail"),
);
const FinanceTab = lazy(
  () => import("../vendors/eventVendor/finance/FinanceTab"),
);
const SettingLayout = lazy(
  () => import("../vendors/eventVendor/setting/SettingLayout"),
);
const OrganizerProfile = lazy(
  () => import("../vendors/eventVendor/setting/OrganizerProfile"),
);
const VenueInformation = lazy(
  () => import("../vendors/eventVendor/setting/VenueInformation"),
);
const BankDetail = lazy(
  () => import("../vendors/eventVendor/setting/BankDetail"),
);
const GSTDetails = lazy(
  () => import("../vendors/eventVendor/setting/GSTDetails"),
);
const EventSecurity = lazy(
  () => import("../vendors/eventVendor/setting/EventSecurity"),
);
const CreateEventLayout = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/CreateEventLayout"),
);
const BasicInformation = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/BasicInformation"),
);
const Media = lazy(
  () => import("../vendors/eventVendor/eventManagement/createEvent/Media"),
);
const DateAndTime = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/DateAndTime"),
);
const VenueDetail = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/VenueDetail"),
);
const SeatLayout = lazy(
  () => import("../vendors/eventVendor/eventManagement/createEvent/SeatLayout"),
);
const TicketSetup = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/TicketSetup"),
);
const Policies = lazy(
  () => import("../vendors/eventVendor/eventManagement/createEvent/Policies"),
);
const AddEventCoupon = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/AddEventCoupon"),
);
const EventCouponDetail = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/EventCouponDetail"),
);
const AddEventCouponFromDetailPage = lazy(
  () =>
    import("../vendors/eventVendor/eventManagement/createEvent/AddEventCouponFromDetailPage"),
);

// ── Holiday Package Vendor ───────────────────────────────────────────────────
const HolidayDashboard = lazy(
  () => import("../vendors/holidayVendor/holidayDashboard/HolidayDashboard"),
);
const PackageList = lazy(
  () => import("../vendors/holidayVendor/packageManagement/PackageList"),
);
const PackageDetail = lazy(
  () => import("../vendors/holidayVendor/packageManagement/PackageDetail"),
);
const CreatePackageLayout = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/CreatePackageLayout"),
);
const PackageBasicInfo = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/BasicInfo"),
);
const PackageMedia = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/Media"),
);
const PackageItinerary = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/Itinerary"),
);
const PackageInclusionsExclusions = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/InclusionsExclusions"),
);
const PackagePricing = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/Pricing"),
);
const PackageCancellationPolicy = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/createPackage/CancellationPolicy"),
);
const PackageBookings = lazy(
  () => import("../vendors/holidayVendor/packageBookings/PackageBookings"),
);
const PackageBookingDetail = lazy(
  () => import("../vendors/holidayVendor/packageBookings/PackageBookingDetail"),
);
const HolidayRevenuePayout = lazy(
  () => import("../vendors/holidayVendor/revenuePayout/HolidayRevenuePayout"),
);
const PackageCommunityDetail = lazy(
  () =>
    import("../vendors/holidayVendor/packageManagement/PackageCommunityDetail"),
);
const HolidaySettingLayout = lazy(
  () => import("../vendors/holidayVendor/Holidaysetting/HolidaySettingLayout"),
);
const HolidayOrganizerProfile = lazy(
  () =>
    import("../vendors/holidayVendor/Holidaysetting/HolidayOrganizerProfile"),
);
const HolidayInformation = lazy(
  () => import("../vendors/holidayVendor/Holidaysetting/HolidayInformation"),
);
const HolidayBankDetail = lazy(
  () => import("../vendors/holidayVendor/Holidaysetting/HolidayBankDetail"),
);
const HolidayGSTDetails = lazy(
  () => import("../vendors/holidayVendor/Holidaysetting/HolidayGSTDetails"),
);
const HolidaySecurity = lazy(
  () => import("../vendors/holidayVendor/Holidaysetting/HolidaySecurity"),
);

// ── Support & Ticket (common to venue/event/hotel) ──────────────────────────
const Requests = lazy(() => import("../vendors/supportTicket/Requests"));
const RequestDetail = lazy(
  () => import("../vendors/supportTicket/RequestDetail"),
);
const AddRequest = lazy(() => import("../vendors/supportTicket/AddRequest"));
// ── Rating (common to venue/event/hotel) ──────────────────────────

const ReviewRating = lazy(() => import("../vendors/reviewRating/Review"));

// ─────────────────────────────────────────────────────────────────────────────
// Route configs
// ─────────────────────────────────────────────────────────────────────────────

const publicRoutes = [
  { path: "/", element: <Login /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/otp", element: <OTP /> },
  { path: "/set-password", element: <NewPassword /> },
  { path: "/signup", element: <SignUp /> },
];

const protectedRoutes = [
  // Venue Vendor Routes
  { path: "/venue-dashboard", element: <VenueDashboard /> },
  { path: "/notification", element: <VendorNotification /> },
  { path: "/my-listings", element: <MyListings /> },
  { path: "/my-listings-detail/:id", element: <MyListingsDetail /> },
  { path: "/venue-booking", element: <VenueBookings /> },
  { path: "/bookings-venue", element: <BookingsOfParticularVenue /> },
  { path: "/venue-booking-detail/:id", element: <VenueBookingDetail /> },
  { path: "/proposal", element: <Proposal /> },
  { path: "/proposal-detail/:id", element: <ProposalDetail /> },
  { path: "/negotiation", element: <Negotiation /> },
  { path: "/negotiation-detail/:id", element: <NegotiationDetail /> },
  { path: "/negotiation-detail/user/:userId", element: <NegotiationDetail /> },
  { path: "/earning-payout", element: <EarningsPayouts /> },
  { path: "/venue-profile", element: <VenueProfile /> },
  { path: "/venue-profile-edit", element: <VenueProfileEdit /> },
  { path: "/venue-bank-edit", element: <VenueBankEdit /> },
  { path: "/venue-bank-detail", element: <VenueBankDetail /> },

  // Hotel Vendor Routes
  { path: "/hotel-dashboard", element: <HotelDashboard /> },
  { path: "/room-management", element: <RoomManagement /> },
  { path: "/hotel-settlement", element: <HotelSettlement /> },
  { path: "/hotel-booking", element: <HotelBookings /> },
  { path: "/bookings-hotel", element: <BookingsOfParticularHotel /> },
  { path: "/hotel-customer", element: <HotelCustomers /> },
  { path: "/hotel-notifications", element: <HotelNotifications /> },
  { path: "/hotel-profile", element: <HotelProfile /> },
  { path: "/hotel-profile-edit", element: <HotelProfileEdit /> },
  { path: "/hotel-reviews", element: <HotelReviews /> },
  { path: "/hotel-settings", element: <HotelSettings /> },
  { path: "/hotel-booking-details/:id?", element: <DetailsHotelBooking /> },
  { path: "/hotel", element: <Hotel /> },
  { path: "/hotel-details/:id?", element: <HotelDetails /> },
  { path: "/hotel-report", element: <HotelReport /> },

  // Event Vendor Routes
  { path: "/event-dashboard", element: <EventDashboard /> },
  { path: "/all-events", element: <EventManagement /> },
  { path: "/event-detail/:id?", element: <EventDetail /> },
  { path: "/event-booking", element: <BookingTab /> },
  { path: "/bookings-event", element: <BookingsOfParticularEvent /> },
  { path: "/event-booking-detail/:id", element: <EventBookingDetail /> },
  { path: "/finance", element: <FinanceTab /> },
  { path: "/event-coupon-detail/:id?", element: <EventCouponDetail /> },
  {
    path: "/event-coupon-form/:eventId/:couponId?",
    element: <AddEventCouponFromDetailPage />,
  },

  // Holiday Package Vendor Routes
  { path: "/holiday-dashboard", element: <HolidayDashboard /> },
  { path: "/holiday-packages", element: <PackageList /> },
  { path: "/holiday-packages-detail/:id", element: <PackageDetail /> },
  { path: "/holiday-bookings", element: <PackageBookings /> },
  { path: "/holiday-booking-detail/:id", element: <PackageBookingDetail /> },
  { path: "/holiday-revenue", element: <HolidayRevenuePayout /> },
  { path: "/holiday-package-detail/:id", element: <PackageCommunityDetail /> },

  // Support & Ticket Routes (common to venue/event/hotel)
  { path: "/requests", element: <Requests /> },
  { path: "/requests/create", element: <AddRequest /> },
  { path: "/requests/:id", element: <RequestDetail /> },

  // Rating Routes (common to venue/event/hotel)
  { path: "/review/:contentType/:id", element: <ReviewRating /> },
];

const createHotelRoutes = {
  path: "/create-hotel/:id?",
  element: <HotelCreateLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="/create-hotel/hotel-basic-info" replace />,
    },
    { path: "hotel-basic-info", element: <HotelBasicInfo /> },
    { path: "property-address", element: <PropertyAddress /> },
    { path: "location-map", element: <LocationMap /> },
    { path: "hotel-gallery", element: <HotelGallery /> },
    { path: "hotel-amenities", element: <HotelAmenities /> },
    { path: "property-rules", element: <PropertyRules /> },
    { path: "hotel-room-management", element: <HotelRoomManagement /> },
    { path: "pricing", element: <Pricing /> },
    { path: "cancellation", element: <Cancellation /> },
    { path: "taxes", element: <Taxes /> },
    { path: "nearby-attractions", element: <NearbyAttractions /> },
    { path: "review-publish", element: <ReviewPublish /> },
  ],
};

// Create Listing Routes (nested)
const createListingRoutes = {
  path: "/create-listing/:id?",
  element: <CreateLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="/create-listing/basic-info" replace />,
    },
    { path: "basic-info", element: <BasicInfo /> },
    { path: "location", element: <Location /> },
    { path: "capacity", element: <Capacity /> },
    { path: "amenities", element: <Amenities /> },
    { path: "booking", element: <BookingDetail /> },
    { path: "rules", element: <Rules /> },
    { path: "highlights", element: <Highlights /> },
    { path: "events", element: <Events /> },
  ],
};

// Venue Setting Routes (nested)
const VenueSettingRoutes = {
  path: "/venue-settings",
  element: <VenueSetting />,
  children: [
    {
      index: true,
      element: <Navigate to="/venue-settings/notifications" replace />,
    },
    { path: "notifications", element: <Notification /> },
    { path: "account", element: <Account /> },
    { path: "security", element: <Security /> },
    { path: "preferences", element: <Preferences /> },
  ],
};

// Event Settings Routes (nested)
const eventSettingsRoutes = {
  path: "/event-settings",
  element: <SettingLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="/event-settings/organizer-profile" replace />,
    },
    { path: "organizer-profile", element: <OrganizerProfile /> },
    { path: "venue-information", element: <VenueInformation /> },
    { path: "bank-details", element: <BankDetail /> },
    { path: "gst-details", element: <GSTDetails /> },
    { path: "security", element: <EventSecurity /> },
  ],
};
const holidaySettingsRoutes = {
  path: "/holiday-settings",
  element: <HolidaySettingLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="/holiday-settings/organizer-profile" replace />,
    },
    { path: "organizer-profile", element: <HolidayOrganizerProfile /> },
    { path: "holiday-information", element: <HolidayInformation /> },
    { path: "bank-details", element: <HolidayBankDetail /> },
    { path: "gst-details", element: <HolidayGSTDetails /> },
    { path: "security", element: <HolidaySecurity /> },
  ],
};

const CreateNewEventRoutes = {
  path: "/create-event/:eventId?",
  element: <CreateEventLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="basic-information" replace />,
    },
    { path: "basic-information", element: <BasicInformation /> },
    { path: "media", element: <Media /> },
    { path: "date-time", element: <DateAndTime /> },
    { path: "venue-details", element: <VenueDetail /> },
    { path: "seat-layout", element: <SeatLayout /> },
    { path: "ticket-setup", element: <TicketSetup /> },
    { path: "policies", element: <Policies /> },
    { path: "coupons", element: <AddEventCoupon /> },
  ],
};

// Create Holiday Package Routes (nested)
const createHolidayPackageRoutes = {
  path: "/create-holiday-package/:packageId?",
  element: <CreatePackageLayout />,
  children: [
    {
      index: true,
      element: <Navigate to="basic-info" replace />,
    },
    { path: "basic-info", element: <PackageBasicInfo /> },
    { path: "media", element: <PackageMedia /> },
    { path: "itinerary", element: <PackageItinerary /> },
    {
      path: "inclusions-exclusions",
      element: <PackageInclusionsExclusions />,
    },
    { path: "pricing", element: <PackagePricing /> },
    {
      path: "cancellation-policy",
      element: <PackageCancellationPolicy />,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Layout Wrapper with Suspense
// ─────────────────────────────────────────────────────────────────────────────

const LayoutWrapper = ({ children }) => (
  <Layout>
    <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
  </Layout>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main App Routes Component
// ─────────────────────────────────────────────────────────────────────────────

const AppRoutes = () => (
  <Routes>
    {/* Public Routes (No Layout) */}
    {publicRoutes.map(({ path, element }) => (
      <Route key={path} path={path} element={element} />
    ))}

    {/* Protected Routes (With Layout) */}
    {protectedRoutes.map(({ path, element }) => (
      <Route
        key={path}
        path={path}
        element={
          <PrivateRoute>
            <LayoutWrapper>{element}</LayoutWrapper>
          </PrivateRoute>
        }
      />
    ))}
    <Route path="*" element={<NotFound />} />

    {/* Create Listing Routes (Nested with Layout) */}
    <Route
      path={createListingRoutes.path}
      element={<LayoutWrapper>{createListingRoutes.element}</LayoutWrapper>}
    >
      {createListingRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>
    <Route
      path={createHotelRoutes.path}
      element={<LayoutWrapper>{createHotelRoutes.element}</LayoutWrapper>}
    >
      {createHotelRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>

    {/* Venue Setting Routes (Nested with Layout) */}
    <Route
      path={VenueSettingRoutes.path}
      element={<LayoutWrapper>{VenueSettingRoutes.element}</LayoutWrapper>}
    >
      {VenueSettingRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>

    <Route
      path={eventSettingsRoutes.path}
      element={<LayoutWrapper>{eventSettingsRoutes.element}</LayoutWrapper>}
    >
      {eventSettingsRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>
    {/* Holiday Setting Routes (Nested with Layout) */}
    <Route
      path={holidaySettingsRoutes.path}
      element={<LayoutWrapper>{holidaySettingsRoutes.element}</LayoutWrapper>}
    >
      {holidaySettingsRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>

    <Route
      path={CreateNewEventRoutes.path}
      element={<LayoutWrapper>{CreateNewEventRoutes.element}</LayoutWrapper>}
    >
      {CreateNewEventRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>

    {/* Create Holiday Package Routes (Nested with Layout) */}
    <Route
      path={createHolidayPackageRoutes.path}
      element={
        <LayoutWrapper>{createHolidayPackageRoutes.element}</LayoutWrapper>
      }
    >
      {createHolidayPackageRoutes.children.map(({ path, element }) => (
        <Route key={path} path={path} element={element} />
      ))}
    </Route>
  </Routes>
);

export default AppRoutes;
